import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider, useStatutory } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { ruleVersions } from "../mock/statutory/mockData";
import { Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function RuleVersionDetail({ rv }) {
  const isBlocked = rv.status === "BLOCKED";

  return (
    <div className="space-y-4" data-testid={`rule-detail-${rv.id}`}>
      {isBlocked && (
        <StatutoryStatePanel
          state="BLOCKED"
          reason={rv.blockingReason}
          oqRef={rv.blockingOQ}
        />
      )}

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Rule Version</div>
          <StatutoryBadge state={rv.status === "ACTIVE" ? "ACTIVE" : "BLOCKED"} size="lg" />
        </div>
        <dl className="space-y-2">
          {[
            ["Family Code", rv.familyCode],
            ["Regime", rv.regime],
            ["Jurisdiction", rv.jurisdiction],
            ["Effective From", rv.effectiveFrom || "—"],
            ["Effective To", rv.effectiveTo || "Open-ended"],
            ["Calculation Basis", isBlocked ? "PENDING LEGAL VERIFICATION" : rv.calculationBasis],
            ["Rate Value", rv.rateValue],
            ["Ceiling", rv.ceilingAmount || "—"],
            ["Floor", rv.floorAmount || "—"],
            ["Evidence Reference", rv.evidenceRef],
            ["Approved By", rv.approvedBy || "—"],
            ["Approved At", rv.approvedAt || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-4 py-1 border-b border-border/30 last:border-0">
              <dt className="text-xs text-muted-foreground shrink-0">{k}</dt>
              <dd className={`text-xs font-mono text-right ${isBlocked && v === "PENDING LEGAL VERIFICATION" ? "text-amber-600 font-semibold" : "text-foreground"}`}>
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {isBlocked && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs text-amber-800 font-semibold">Fields showing "PENDING LEGAL VERIFICATION" are not blank by accident.</p>
          <p className="text-xs text-amber-700 mt-1">These values require primary-source legal verification before they can be configured. Assumed rates from secondary sources are not used.</p>
        </div>
      )}

      {!isBlocked && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Immutability Notice</div>
          <p className="text-xs text-muted-foreground">All fields above are IMMUTABLE once a version is created. Changes require creating a new superseding version. No edit button exists for active rule versions.</p>
        </div>
      )}
    </div>
  );
}

function RuleVersionsContent() {
  const { currentRole } = useStatutory();
  const [selected, setSelected] = useState(ruleVersions[0]);
  const [filter, setFilter] = useState("ALL");

  const filtered = filter === "ALL" ? ruleVersions : ruleVersions.filter((r) => r.status === filter || r.regime === filter);
  const filters = ["ALL", "ACTIVE", "BLOCKED"];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* List */}
      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${filter === f ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs" data-testid="rule-versions-table">
            <thead className="bg-muted/40">
              <tr>
                {["Family", "Regime", "Rate", "Eff. From", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((rv) => (
                <tr
                  key={rv.id}
                  className={`hover:bg-muted/30 cursor-pointer transition-colors ${selected?.id === rv.id ? "bg-primary/5" : ""} ${rv.status === "BLOCKED" ? "bg-red-50/30" : ""}`}
                  onClick={() => setSelected(rv)}
                  data-testid={`rv-row-${rv.id}`}
                >
                  <td className="px-3 py-2.5 font-mono max-w-[160px] truncate">{rv.familyCode}</td>
                  <td className="px-3 py-2.5 max-w-[120px] truncate">{rv.regime}</td>
                  <td className={`px-3 py-2.5 font-mono ${rv.status === "BLOCKED" ? "text-amber-600 font-bold" : ""}`}>
                    {rv.status === "BLOCKED" ? (
                      <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Blocked</span>
                    ) : rv.rateValue}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{rv.effectiveFrom || "—"}</td>
                  <td className="px-3 py-2.5">
                    <StatutoryBadge state={rv.status === "ACTIVE" ? "ACTIVE" : "BLOCKED"} />
                  </td>
                  <td className="px-3 py-2.5 text-primary text-xs">View</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {currentRole.canCreateRuleVersions && (
          <button
            onClick={() => toast.info("Prototype: Create rule version — no backend.")}
            className="text-xs border border-border rounded px-3 py-1.5 hover:bg-muted"
            data-testid="create-rule-btn"
          >
            Create New Version
          </button>
        )}
        {!currentRole.canCreateRuleVersions && (
          <p className="text-xs text-muted-foreground italic flex items-center gap-1">
            <Lock className="h-3 w-3" />
            {currentRole.id === "AUDITOR" ? "Auditor — read-only access." : "Statutory Analyst — rule version creation requires Admin role."}
          </p>
        )}
      </div>

      {/* Detail */}
      <div className="lg:col-span-2">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Version Detail</div>
        {selected ? <RuleVersionDetail rv={selected} /> : <p className="text-sm text-muted-foreground">Select a rule version.</p>}
      </div>
    </div>
  );
}

export default function StatutoryRuleVersions() {
  return (
    <StatutoryProvider>
      <PageHeader eyebrow="Statutory Configuration" title="Rule Version Configuration" description="Statutory rule versions — mock prototype. BLOCKED entries show unresolved legal states." />
      <StatutoryShell>
        <RuleVersionsContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
